import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const ChatbotPage = ({ apiBase }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [channelPartners, setCPs] = useState([]);
  const [collaterals, setCollaterals] = useState([]);
  const [selectedCollateral, setSelectedCollateral] = useState('');
  const [searchType, setSearchType] = useState('client');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchPanel, setShowSearchPanel] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Load reference data
    axios.get(`${apiBase}/clients/list`).then(r => setClients(r.data || [])).catch(() => {});
    axios.get(`${apiBase}/channel-partners`).then(r => setCPs(r.data || [])).catch(() => {});
    axios.get(`${apiBase}/collaterals`).then(r => setCollaterals(r.data || [])).catch(() => {});

    // Welcome message
    setMessages([{
      id: 1, role: 'assistant', timestamp: new Date().toISOString(),
      content: 'Welcome to the Kohinoor@Patel Estate Assistant! 🏠\n\nI can help you with:\n• 🔍 Search clients or channel partners\n• 📎 Share collaterals (brochures, floor plans)\n• 📊 Quick project info & pricing\n• 📝 Create follow-ups or notes\n\nUse the search panel on the right to look up clients or CPs, or just type your question below.'
    }]);
  }, []);

  // Auto-populate search results when data loads
  useEffect(() => {
    if (searchType === 'client' && clients.length > 0) setSearchResults(clients);
    else if (searchType === 'cp' && channelPartners.length > 0) setSearchResults(channelPartners);
  }, [clients, channelPartners, searchType]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSearch = (query, type) => {
    const q = (query !== undefined ? query : searchQuery).toLowerCase().trim();
    const t = type || searchType;
    let results = [];
    if (t === 'client') {
      results = q ? clients.filter(c => c.name?.toLowerCase().includes(q) || c.phone?.includes(q)) : clients;
    } else {
      results = q ? channelPartners.filter(cp => cp.name?.toLowerCase().includes(q) || cp.phone?.includes(q) || cp.company_name?.toLowerCase().includes(q)) : channelPartners;
    }
    setSearchResults(results);
  };

  const insertClientRef = (item) => {
    const ref = searchType === 'client'
      ? `Client: ${item.name} (${item.phone || 'No phone'})`
      : `CP: ${item.name} ${item.company_name ? '(' + item.company_name + ')' : ''} — ${item.phone || ''}`;
    setInputMessage(prev => prev + (prev ? '\n' : '') + ref);
    inputRef.current?.focus();
  };

  const insertCollateral = () => {
    if (!selectedCollateral) return;
    const col = collaterals.find(c => String(c.id) === String(selectedCollateral));
    if (col) {
      const ref = `📎 Collateral: ${col.name || col.title || col.file_name} (${col.type || 'document'})`;
      setInputMessage(prev => prev + (prev ? '\n' : '') + ref);
      inputRef.current?.focus();
    }
  };

  const handleSend = async () => {
    if (!inputMessage.trim()) return;
    const userMsg = { id: Date.now(), role: 'user', content: inputMessage.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setLoading(true);

    // Simulate bot response (placeholder — real AI integration later)
    setTimeout(() => {
      const botReply = generatePlaceholderResponse(userMsg.content);
      setMessages(prev => [...prev, {
        id: Date.now() + 1, role: 'assistant', content: botReply, timestamp: new Date().toISOString()
      }]);
      setLoading(false);
    }, 800);
  };

  const generatePlaceholderResponse = (msg) => {
    const lower = msg.toLowerCase();
    if (lower.includes('price') || lower.includes('rate') || lower.includes('cost')) {
      return '📊 **Kohinoor@Patel Estate Pricing:**\n\n• 1BHK: ₹1.26 Cr - ₹1.31 Cr (467-487 sqft)\n• 2BHK: ₹1.76 Cr - ₹1.90 Cr (652-705 sqft)\n• 2BHK+Study: ₹2.03 Cr (752 sqft)\n• 3BHK: ₹2.24 Cr (830 sqft)\n\nBase rate: ₹27,000/sqft + floor rise ₹200/floor\n\n_This is project data. For live quotes, contact the sales team._';
    }
    if (lower.includes('wing') || lower.includes('tower') || lower.includes('layout')) {
      return '🏗️ **Project Layout:**\n\n• **Wing 1** — 1BHK (Type A, B), 2BHK (Type A)\n• **Wing 2** — 1BHK (Type C), 2BHK (Type B, C)\n• **Wing 3** — 2BHK (Type D, E), 2BHK+Study\n• **Wing 4** — 1BHK, 2BHK, 3BHK\n\nAll wings have 35 floors with 3 units/floor.';
    }
    if (lower.includes('follow') || lower.includes('schedule')) {
      return '📞 To create a follow-up:\n\n1. Go to **Follow-ups** in the sidebar\n2. Click **+ New Follow-up**\n3. Select the client and schedule date\n\nWould you like me to help with anything else?';
    }
    if (lower.includes('brochure') || lower.includes('collateral') || lower.includes('floor plan')) {
      return '📎 You can share collaterals using the dropdown on the right panel.\n\nAvailable types: Brochures, Floor Plans, Price Lists, Cost Sheets.\n\nSelect a collateral and click "Insert" to reference it in the chat.';
    }
    if (lower.includes('client') && (lower.includes('search') || lower.includes('find'))) {
      return '🔍 Use the **Search Panel** on the right to find clients or channel partners by name or phone number.\n\nClick on a result to insert their details into the chat.';
    }
    return '🤖 I received your message. The full AI chatbot is being configured.\n\nFor now, you can:\n• Search **clients** or **channel partners** using the right panel\n• Insert **collaterals** from the dropdown\n• Ask about **pricing**, **layouts**, or **project info**\n\n_Full conversational AI coming soon!_';
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 70px)', gap: '0', background: '#f8fafc' }}>
      {/* Chat Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
          color: '#fff', display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🤖</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px' }}>Kohinoor Assistant</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>AI-powered sales assistant • Always available</div>
          </div>
          <button onClick={() => setShowSearchPanel(!showSearchPanel)}
            style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            {showSearchPanel ? '◀ Hide Panel' : '▶ Show Panel'}
          </button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {messages.map(msg => (
            <div key={msg.id} style={{
              display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: '8px', alignItems: 'flex-end'
            }}>
              {msg.role === 'assistant' && (
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🤖</div>
              )}
              <div style={{
                maxWidth: '70%', padding: '12px 16px', borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                background: msg.role === 'user' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#fff',
                color: msg.role === 'user' ? '#fff' : '#1e293b',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontSize: '14px', lineHeight: '1.5',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word'
              }}>
                {msg.content}
                <div style={{ fontSize: '10px', opacity: 0.6, marginTop: '6px', textAlign: 'right' }}>{formatTime(msg.timestamp)}</div>
              </div>
              {msg.role === 'user' && (
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#475569', flexShrink: 0 }}>You</div>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🤖</div>
              <div style={{ padding: '12px 20px', background: '#fff', borderRadius: '18px 18px 18px 4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1.2s infinite' }} />
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1.2s infinite 0.2s' }} />
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1.2s infinite 0.4s' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', background: '#fff' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <textarea ref={inputRef} value={inputMessage} onChange={e => setInputMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Type your message... (Shift+Enter for new line)"
              rows="2" style={{
                flex: 1, padding: '12px 16px', borderRadius: '12px', border: '2px solid #e2e8f0', resize: 'none',
                fontSize: '14px', fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.2s',
                lineHeight: '1.4'
              }}
              onFocus={e => e.target.style.borderColor = '#3b82f6'}
              onBlur={e => e.target.style.borderColor = '#e2e8f0'}
            />
            <button onClick={handleSend} disabled={!inputMessage.trim() || loading}
              style={{
                padding: '12px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: inputMessage.trim() ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e2e8f0',
                color: inputMessage.trim() ? '#fff' : '#94a3b8', fontWeight: 600, fontSize: '14px',
                transition: 'all 0.2s', minWidth: '80px'
              }}>
              Send ➤
            </button>
          </div>
        </div>
      </div>

      {/* Right Panel — Search & Collaterals */}
      {showSearchPanel && (
        <div style={{
          width: '320px', borderLeft: '1px solid #e2e8f0', background: '#fff',
          display: 'flex', flexDirection: 'column', flexShrink: 0
        }}>
          {/* Search Section */}
          <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>🔍 Quick Search</h3>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
              <button onClick={() => { setSearchType('client'); setSearchQuery(''); handleSearch('', 'client'); }}
                style={{
                  flex: 1, padding: '6px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  background: searchType === 'client' ? '#3b82f6' : '#f1f5f9', color: searchType === 'client' ? '#fff' : '#64748b'
                }}>👤 Clients</button>
              <button onClick={() => { setSearchType('cp'); setSearchQuery(''); handleSearch('', 'cp'); }}
                style={{
                  flex: 1, padding: '6px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  background: searchType === 'cp' ? '#3b82f6' : '#f1f5f9', color: searchType === 'cp' ? '#fff' : '#64748b'
                }}>🤝 Channel Partners</button>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); handleSearch(e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                placeholder={searchType === 'client' ? 'Name or phone...' : 'Name, company, phone...'}
                style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', outline: 'none' }}
              />
              <button onClick={handleSearch}
                style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                Go
              </button>
            </div>
          </div>

          {/* Search Results */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
            {searchResults.length > 0 ? (
              <div>
                <div style={{ padding: '8px 16px', fontSize: '11px', color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                </div>
                {searchResults.map(item => (
                  <div key={item.id} onClick={() => insertClientRef(item)}
                    style={{
                      padding: '10px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>{item.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                      {item.phone || 'No phone'} {item.company_name ? ` • ${item.company_name}` : ''}
                    </div>
                    <div style={{ fontSize: '10px', color: '#3b82f6', marginTop: '2px' }}>Click to insert ↩</div>
                  </div>
                ))}
              </div>
            ) : null}
            {searchResults.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                {searchQuery ? 'No results found. Try a different search term.' : 'Loading...'}
              </div>
            )}
          </div>

          {/* Collaterals Section */}
          <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>📎 Collaterals</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <select value={selectedCollateral} onChange={e => setSelectedCollateral(e.target.value)}
                style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '12px', background: '#fff' }}>
                <option value="">-- Select Collateral --</option>
                {collaterals.map(col => (
                  <option key={col.id} value={col.id}>
                    {col.name || col.title || col.file_name || `Collateral #${col.id}`} ({col.type || 'doc'})
                  </option>
                ))}
              </select>
              <button onClick={insertCollateral} disabled={!selectedCollateral}
                style={{
                  padding: '8px 12px', borderRadius: '8px', border: 'none', cursor: selectedCollateral ? 'pointer' : 'not-allowed',
                  background: selectedCollateral ? '#16a34a' : '#e2e8f0', color: selectedCollateral ? '#fff' : '#94a3b8',
                  fontSize: '12px', fontWeight: 600
                }}>
                Insert
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>⚡ Quick Actions</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {[
                { label: 'Pricing', q: 'Show me the pricing for all configurations' },
                { label: 'Floor Plan', q: 'Show wing layouts and floor plans' },
                { label: 'Amenities', q: 'What amenities are available?' },
                { label: 'Payment Plan', q: 'What payment plans are available?' },
                { label: 'RERA Info', q: 'What is the RERA number and details?' },
                { label: 'Location', q: 'Tell me about the location and connectivity' },
              ].map((action, i) => (
                <button key={i} onClick={() => { setInputMessage(action.q); inputRef.current?.focus(); }}
                  style={{
                    padding: '5px 10px', borderRadius: '16px', border: '1px solid #e2e8f0',
                    background: '#f8fafc', color: '#475569', fontSize: '11px', cursor: 'pointer',
                    fontWeight: 500, transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >{action.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotPage;
